import { Link } from 'react-router-dom'
import { C } from '../lib/landingColors'
import {
  toMarketingLocale,
  useLandingLocale,
  type MarketingLocale,
  type Multilingual,
} from '../lib/marketingLocale'
import { marketingPath } from '../lib/marketingPages'
import { FOUNDING_PROGRAM_OPEN, INFO_EMAIL } from '../config/marketing'
import { LogoMark } from './LogoMark'
import { SwissMadeBadge } from './SwissMadeBadge'

/**
 * The LABELS translate six ways; the pages they link to are EN/DE.
 *
 * That mismatch is deliberate: a French reader clicking "Presse" gets the
 * English press page, which is the nearest document that exists, and a labelled
 * link in their own language is strictly better than an English label in front
 * of the same English page. Since 2026-09-09 the legal pages are pairs too, so
 * "AGB" and "Datenschutz" finally reach German documents instead of pointing a
 * German label at English-only text.
 */
const LABELS: Multilingual<{
  terms: string
  privacy: string
  subprocessors: string
  press: string
  compare: string
  founding: string
  madeIn: string
  /**
   * Shown under the links when the footer's language has no legal documents of
   * its own. Decision D26: the Terms and the Privacy Policy stay English and
   * German, so a Hungarian reader is told so in Hungarian before they click
   * "Terms" and land on English. Null where the language needs no such line
   * — en/de have their documents, and fr/it/es/pt shipped without one before
   * the decision and are not widened by it.
   */
  legalLanguageNote: string | null
}> = {
  en: {
    terms: 'Terms',
    privacy: 'Privacy',
    subprocessors: 'Subprocessors',
    press: 'Press',
    compare: 'Compare',
    founding: 'Founding studios',
    madeIn: 'Made in Zürich.',
    legalLanguageNote: null,
  },
  de: {
    terms: 'AGB',
    privacy: 'Datenschutz',
    subprocessors: 'Unterauftragsverarbeiter',
    press: 'Presse',
    compare: 'Vergleich',
    founding: 'Founding-Studios',
    madeIn: 'Made in Zürich.',
    legalLanguageNote: null,
  },
  fr: {
    terms: 'CGU',
    privacy: 'Confidentialité',
    subprocessors: 'Sous-traitants',
    press: 'Presse',
    compare: 'Comparatif',
    founding: 'Studios fondateurs',
    madeIn: 'Made in Zürich.',
    legalLanguageNote: null,
  },
  it: {
    terms: 'Condizioni',
    privacy: 'Privacy',
    subprocessors: 'Sub-responsabili',
    press: 'Stampa',
    compare: 'Confronto',
    founding: 'Studi fondatori',
    madeIn: 'Made in Zürich.',
    legalLanguageNote: null,
  },
  es: {
    terms: 'Condiciones',
    privacy: 'Privacidad',
    subprocessors: 'Subencargados',
    press: 'Prensa',
    compare: 'Comparativa',
    founding: 'Estudios fundadores',
    madeIn: 'Made in Zürich.',
    legalLanguageNote: null,
  },
  pt: {
    terms: 'Termos',
    privacy: 'Privacidade',
    subprocessors: 'Suboperadores',
    press: 'Imprensa',
    compare: 'Comparativo',
    founding: 'Estúdios fundadores',
    madeIn: 'Made in Zürich.',
    legalLanguageNote: null,
  },
  hu: {
    terms: 'ÁSZF',
    privacy: 'Adatvédelem',
    subprocessors: 'Adatfeldolgozók',
    press: 'Sajtó',
    compare: 'Összehasonlítás',
    founding: 'Alapító stúdiók',
    madeIn: 'Zürichben készült.',
    legalLanguageNote: 'Az ÁSZF és az Adatvédelmi tájékoztató angol és német nyelven érhető el.',
  },
  nl: {
    terms: 'Voorwaarden',
    privacy: 'Privacy',
    subprocessors: 'Subverwerkers',
    press: 'Pers',
    compare: 'Vergelijking',
    founding: 'Oprichtersstudio’s',
    madeIn: 'Gemaakt in Zürich.',
    legalLanguageNote: 'De voorwaarden en het privacybeleid zijn beschikbaar in het Engels en Duits.',
  },
  pl: {
    terms: 'Regulamin',
    privacy: 'Prywatność',
    subprocessors: 'Podprocesorzy',
    press: 'Prasa',
    compare: 'Porównanie',
    founding: 'Studia założycielskie',
    madeIn: 'Stworzone w Zurychu.',
    legalLanguageNote: 'Regulamin i Polityka prywatności są dostępne w języku angielskim i niemieckim.',
  },
  cs: {
    terms: 'Podmínky',
    privacy: 'Soukromí',
    subprocessors: 'Další zpracovatelé',
    press: 'Tisk',
    compare: 'Srovnání',
    founding: 'Zakládající studia',
    madeIn: 'Vytvořeno v Curychu.',
    legalLanguageNote: 'Podmínky a zásady ochrany osobních údajů jsou k dispozici v angličtině a němčině.',
  },
}

/**
 * `locale` overrides the visitor's UI locale on pages whose language comes from
 * the URL — see MarketingShell. Every link below is then resolved to the
 * same-language sibling, so a reader who landed on a German page never gets
 * bounced into English by the footer.
 */
export function LandingFooter({ locale: routeLocale }: { locale?: MarketingLocale } = {}) {
  const uiLocale = useLandingLocale()
  const locale = routeLocale ?? uiLocale
  const l = LABELS[locale]
  // The documents exist in two languages, so the six-way label locale narrows
  // back down to pick a target: German reads German, everything else English.
  const docLocale = toMarketingLocale(locale)
  // Deep-link into the comparison page written in the reader's own language.
  const comparePath = docLocale === 'de' ? '/vergleich/pixieset-alternative' : '/compare/honeybook-alternative-europe'

  return (
    <footer className="py-12" style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <LogoMark className="w-6 h-6" />
            <span className="text-sm font-bold" style={{ color: C.text }}>
              Studio<span style={{ color: '#a472ff' }}>Flow</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm" style={{ color: C.muted }}>
            <Link to={marketingPath('/terms', docLocale)} className="hover:text-white transition-colors">{l.terms}</Link>
            <Link to={marketingPath('/privacy', docLocale)} className="hover:text-white transition-colors">{l.privacy}</Link>
            <Link to={marketingPath('/legal/subprocessors', docLocale)} className="hover:text-white transition-colors">{l.subprocessors}</Link>
            <Link to={marketingPath('/press', docLocale)} className="hover:text-white transition-colors">{l.press}</Link>
            <Link to={comparePath} className="hover:text-white transition-colors">{l.compare}</Link>
            {/* The program link disappears with the program itself, so the footer
                never points at a page that says "closed". */}
            {FOUNDING_PROGRAM_OPEN && (
              <Link to={marketingPath('/founding', docLocale)} className="hover:text-white transition-colors">{l.founding}</Link>
            )}
            <a href={`mailto:${INFO_EMAIL}`} className="hover:text-white transition-colors">{INFO_EMAIL}</a>
          </div>
          <p className="text-xs" style={{ color: '#8c8599' }}>&copy; {new Date().getFullYear()} StudioFlow. {l.madeIn}</p>
        </div>

        {l.legalLanguageNote && (
          <p data-testid="landing-legal-language-note" className="mt-6 text-xs text-center" style={{ color: '#8c8599' }}>
            {l.legalLanguageNote}
          </p>
        )}

        {/* swiss made software slot #1 (pricing section holds #2). Renders nothing
            until the label is granted — see config/marketing.ts. */}
        <div className="mt-8 flex justify-center">
          <SwissMadeBadge height={36} />
        </div>
      </div>
    </footer>
  )
}
